/* A group's events.
 *
 * "Events" is one of the six checkboxes in the client's Features block.
 *
 * THREE ANSWERS, NOT A CHECKBOX. Going / Maybe / Can't go, because "maybe" is
 * the answer people actually give and folding it into "not going" loses the
 * number an organiser most wants to see.
 *
 * Past events stay in the list rather than disappearing on their date — an
 * organiser wants to look back at what happened, and vanishing rows read as
 * data loss.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarDays, ChevronRight, ClipboardCheck, Loader2, MapPin, Plus, Trash2 } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { Field, TextArea } from '@/components/ui/field'
import { Modal } from '@/components/ui/modal'
import {
  useCreateEvent,
  useDeleteEvent,
  useGroupEvents,
  useCan,
  useMe,
  useRsvp,
  useRsvps,
  useOccurrences,
  useSetEventAttendance,
} from '@/api/queries'
import { useAttendanceAccess } from '@/features/attendance/AttendancePage'
import { cn, localDateTimeNow } from '@/lib/utils'
import type { GroupEvent, RsvpStatus } from '@/types/api'

const ANSWERS: { value: RsvpStatus; label: string }[] = [
  { value: 'going', label: 'Going' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'not_going', label: "Can't go" },
]

/** "Sat 16 Aug, 5:00 pm" — the day name matters more than the year for
 *  something happening this month. */
function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function EventsPanel({
  churchId,
  groupId,
}: {
  churchId: string
  groupId: string
}) {
  const [adding, setAdding] = useState(false)
  const events = useGroupEvents(churchId, groupId)

  /* The API refuses this without events.create — see assert_permission. A
     button that appears and then 403s reads as the app being broken, rather
     than as something this person is not allowed to do. */
  const can = useCan(churchId, groupId)

  return (
    <div className="space-y-s3">
      {can('events.create') ? (
        <div className="flex justify-end">
          <Button variant="primary" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            New event
          </Button>
        </div>
      ) : null}

      {events.isLoading ? (
        <div className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </div>
      ) : null}

      {!events.isLoading && !events.data?.length ? (
        <Card className="p-s8 text-center">
          <CalendarDays className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
          <p className="mt-s3 text-md font-bold">No events yet</p>
          <p className="mt-1 text-base text-ink-2">
            Everyone in the group will see what you add here.
          </p>
        </Card>
      ) : null}

      {events.data?.map((e) => (
        <EventCard
          key={e.id}
          event={e}
          churchId={churchId}
          groupId={groupId}
        />
      ))}

      {adding ? (
        <NewEventModal
          churchId={churchId}
          groupId={groupId}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </div>
  )
}

function EventCard({
  event,
  churchId,
  groupId,
}: {
  event: GroupEvent
  churchId: string
  groupId: string
}) {
  const rsvp = useRsvp(churchId, groupId)
  const remove = useDeleteEvent(churchId, groupId)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [showWho, setShowWho] = useState(false)
  const { data: me } = useMe()
  const can = useCan(churchId, groupId)

  const past = new Date(event.starts_at) < new Date()

  return (
    <Card className={cn('p-s4', past && 'opacity-70')}>
      <div className="flex items-start gap-s3">
        <div className="min-w-0 flex-1">
          <h3 className="text-md font-bold">{event.title}</h3>
          <p className="mt-1 flex flex-wrap items-center gap-x-s3 gap-y-1 text-base text-ink-2">
            <span className="flex items-center gap-1.5">
              <CalendarDays className="h-4 w-4" aria-hidden />
              {when(event.starts_at)}
              {past ? ' · past' : ''}
            </span>
            {event.location ? (
              <span className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4" aria-hidden />
                {event.location}
              </span>
            ) : null}
          </p>
          {event.description ? (
            <p className="mt-s2 whitespace-pre-wrap text-base leading-normal">
              {event.description}
            </p>
          ) : null}
        </div>

        {/* PERMISSION FIRST, OWNERSHIP SECOND. events.create is the gate —
            the event belongs to the GROUP, so a demoted moderator loses the
            ability with the role. Then whose it is, unless they hold
            posts.moderate ("Delete anyone's post"), which is exactly what
            that permission is for. Ownership by ID, never by display name. */}
        {can('events.create') &&
        (can('posts.moderate') || (me && event.created_by_id === me.id)) ? (
          <button
            onClick={() => setConfirmDelete(true)}
            aria-label={`Delete ${event.title}`}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover-2 hover:text-danger"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
      </div>

      <Confirm
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete "${event.title}"?`}
        body="Everyone in the group loses it, along with their answers. This cannot be undone."
        confirmLabel="Delete event"
        loading={remove.isPending}
        onConfirm={() => remove.mutate(event.id)}
      />

      {/* The counts are a button: a number tells an organiser how many chairs
          to put out, a NAME tells them who to chase. */}
      {event.going + event.maybe + event.not_going > 0 ? (
        <button
          onClick={() => setShowWho(true)}
          className="mt-s3 text-base font-semibold text-brand hover:underline"
        >
          {event.going} going · {event.maybe} maybe · {event.not_going} can't go
        </button>
      ) : null}

      {showWho ? (
        <RsvpList
          churchId={churchId}
          groupId={groupId}
          event={event}
          onClose={() => setShowWho(false)}
        />
      ) : null}

      {can('events.create') || event.take_attendance ? (
        <TakeAttendance
          churchId={churchId}
          groupId={groupId}
          event={event}
          canSwitch={can('events.create')}
        />
      ) : null}

      <div className="mt-s3 flex flex-wrap items-center gap-s2">
        {ANSWERS.map(({ value, label }) => {
          const on = event.my_rsvp === value
          const count =
            value === 'going'
              ? event.going
              : value === 'maybe'
                ? event.maybe
                : event.not_going
          return (
            <button
              key={value}
              onClick={() => rsvp.mutate({ eventId: event.id, status: value })}
              disabled={rsvp.isPending}
              aria-pressed={on}
              className={cn(
                'min-h-[38px] rounded-pill px-s4 text-base font-semibold transition-colors',
                // The chosen answer is a SOLID fill. Tinted-with-a-blue-border
                // sat too close to the unchosen outline to read at a glance —
                // you had to compare two chips to tell which was yours.
                on
                  ? 'bg-brand text-ink-on-brand hover:bg-brand-hover'
                  : 'border border-line text-ink-2 hover:bg-hover',
              )}
            >
              {label}
              {count ? ` · ${count}` : ''}
            </button>
          )
        })}
      </div>
    </Card>
  )
}

/** The organiser's switch, and the way to that day's report once it has
 *  one. Members see nothing here: they check in from Services. */
function TakeAttendance({
  churchId,
  groupId,
  event,
  canSwitch,
}: {
  churchId: string
  groupId: string
  event: GroupEvent
  canSwitch: boolean
}) {
  const set = useSetEventAttendance(churchId, groupId)
  const access = useAttendanceAccess(churchId)
  const on = Boolean(event.take_attendance)
  /* The report belongs to the service day the event becomes. Days are
     opened on the day itself, so before then there is nothing to link. */
  const days = useOccurrences(churchId, { pageSize: 50 }, on && access.mark)
  const occurrence = days.data?.items.find(
    (o) => o.id === event.attendance_occurrence_id,
  )

  if (!canSwitch && !(on && occurrence && access.mark)) return null

  return (
    <div className="mt-s3 flex flex-wrap items-center gap-s3 rounded border border-line-2 px-s3 py-s2">
      {canSwitch ? (
        <label className="flex cursor-pointer items-center gap-s2 text-base font-semibold">
          <button
            type="button"
            role="switch"
            aria-checked={on}
            disabled={set.isPending}
            onClick={() => set.mutate({ eventId: event.id, enabled: !on })}
            className={cn(
              'relative h-6 w-11 shrink-0 rounded-pill transition-colors disabled:opacity-60',
              on ? 'bg-brand' : 'bg-hover-2',
            )}
          >
            <span
              className={cn(
                'absolute top-0.5 h-5 w-5 rounded-pill bg-white shadow-1 transition-all',
                on ? 'left-[22px]' : 'left-0.5',
              )}
            />
          </button>
          <ClipboardCheck className="h-4 w-4 text-ink-2" aria-hidden />
          Take attendance
        </label>
      ) : null}
      {on && occurrence && access.mark ? (
        <Link
          to={`/attendance/days/${occurrence.id}`}
          className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
        >
          {occurrence.counts.present} of {occurrence.counts.expected} present
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      ) : on && canSwitch ? (
        <span className="text-sm text-ink-2">
          Members of the group can check in on the day.
        </span>
      ) : null}
      {set.isError ? (
        <p role="alert" className="w-full text-sm text-danger">
          {(set.error as Error).message}
        </p>
      ) : null}
    </div>
  )
}

/** The names behind the numbers, grouped by answer. */
function RsvpList({
  churchId,
  groupId,
  event,
  onClose,
}: {
  churchId: string
  groupId: string
  event: GroupEvent
  onClose: () => void
}) {
  const people = useRsvps(churchId, groupId, event.id)

  const sections: { status: RsvpStatus; label: string }[] = [
    { status: 'going', label: 'Going' },
    { status: 'maybe', label: 'Maybe' },
    { status: 'not_going', label: "Can't go" },
  ]

  return (
    <Modal open onOpenChange={(next) => !next && onClose()} title={event.title}>
      {people.isLoading ? (
        <div className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </div>
      ) : null}

      {sections.map(({ status, label }) => {
        const rows = (people.data ?? []).filter((p) => p.status === status)
        if (!rows.length) return null
        return (
          <div key={status}>
            <h3 className="border-b border-line-2 bg-surface-2 px-s4 py-s2 text-sm font-bold uppercase tracking-wide text-ink-2">
              {label} · {rows.length}
            </h3>
            <ul className="divide-y divide-line-2">
              {rows.map((p) => (
                <li key={p.id} className="flex items-center gap-s3 px-s4 py-s3">
                  <Avatar name={p.name} src={p.avatar_url} size="sm" />
                  <span className="truncate text-md font-semibold">{p.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )
      })}

      {!people.isLoading && !people.data?.length ? (
        <p className="px-s4 py-s6 text-center text-base text-ink-2">
          Nobody has answered yet.
        </p>
      ) : null}
    </Modal>
  )
}

function NewEventModal({
  churchId,
  groupId,
  onClose,
}: {
  churchId: string
  groupId: string
  onClose: () => void
}) {
  const [title, setTitle] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [location, setLocation] = useState('')
  const [description, setDescription] = useState('')
  const create = useCreateEvent(churchId, groupId)

  function submit() {
    if (!title.trim() || !startsAt || create.isPending) return
    create.mutate(
      {
        title: title.trim(),
        // datetime-local has no timezone; the browser's own offset is the
        // right one, since whoever types it means their local time.
        starts_at: new Date(startsAt).toISOString(),
        location: location.trim() || null,
        description: description.trim() || null,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title="New event"
      guardClose={Boolean(title.trim() || description.trim())}
      footer={
        <Button
          variant="primary"
          size="lg"
          full
          onClick={submit}
          disabled={!title.trim() || !startsAt}
          loading={create.isPending}
        >
          Create event
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        <Field
          id="event-title"
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Youth camp"
          autoFocus
          required
        />
        <Field
          id="event-when"
          label="When"
          type="datetime-local"
          value={startsAt}
          onChange={(e) => setStartsAt(e.target.value)}
          // NOTHING BEFORE NOW. An event is an invitation, so a past one has
          // nobody left to invite — the picker used to offer today and every
          // day before it. `min` greys out the earlier dates in the browser's
          // own calendar rather than waiting to reject the form afterwards.
          //
          // The server checks it again: min is a courtesy, not a boundary.
          min={localDateTimeNow()}
          required
        />
        <Field
          id="event-where"
          label="Where (optional)"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Church hall"
        />
        <TextArea
          id="event-description"
          label="Details (optional)"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {create.isError ? (
          <p className="text-base text-danger">
            That did not save. Check the details and try again.
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
